import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { MailX, Loader2, XCircle, CalendarHeart, Megaphone, UserX } from "lucide-react";
import { useBranding } from "@/lib/branding";

const UD_SEAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png";

export default function Unsubscribe() {
  const { token } = useParams();
  const b = useBranding();
  const [info, setInfo] = useState(null); // null=loading, false=invalid, obj
  const [events, setEvents] = useState(true);
  const [announcements, setAnnouncements] = useState(true);
  const [busy, setBusy] = useState(false);

  const apply = (d) => { setInfo(d); setEvents(d.events); setAnnouncements(d.announcements); };

  useEffect(() => {
    api.get(`/unsubscribe/${token}`).then((r) => apply(r.data)).catch(() => setInfo(false));
  }, [token]);

  const post = async (payload, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/unsubscribe/${token}`, payload);
      apply({ ...info, ...data });
      toast.success(msg);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const savePrefs = () => post({ events, announcements }, "Your email preferences were saved");
  const unsubAll = () => post({ opt_out: true }, "You've been unsubscribed from all emails");
  const resubscribe = () => post({ opt_out: false }, "You're subscribed again");
  const reportWrong = () => {
    if (window.confirm("Report that these emails are reaching the wrong person? We'll stop all emails to this address and flag the record for our team to review.")) {
      post({ wrong_person: true }, "Thanks for letting us know — we've stopped emailing this address.");
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card rounded-xl shadow-xl border border-border overflow-hidden fade-up">
        <div className="bg-secondary px-6 py-6 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center p-1">{b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : null}</div>
          <div className="text-white">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent">{b.university_name}</div>
            <div className="font-serif text-xl">Email Preferences</div>
          </div>
        </div>
        <div className="p-6">
          {info === null && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading your preferences…</div>}

          {info === false && (
            <div className="text-center py-6" data-testid="unsub-invalid">
              <XCircle className="h-10 w-10 text-destructive mx-auto" />
              <p className="mt-3 text-sm text-muted-foreground">This unsubscribe link is not valid.</p>
            </div>
          )}

          {info && info !== false && info.opted_out && (
            info.wrong_person ? (
              <div className="text-center py-4" data-testid="unsub-wrong-confirm">
                <UserX className="h-10 w-10 text-muted-foreground mx-auto" />
                <h2 className="font-serif text-xl tracking-tight mt-3">Thanks for letting us know</h2>
                <p className="text-sm text-muted-foreground mt-2">We've stopped all emails to {info.email || "this address"} and flagged this record for our team to review and correct. Sorry for the mix-up.</p>
              </div>
            ) : (
            <div className="text-center py-4" data-testid="unsub-optedout">
              <MailX className="h-10 w-10 text-muted-foreground mx-auto" />
              <h2 className="font-serif text-xl tracking-tight mt-3">You're unsubscribed</h2>
              <p className="text-sm text-muted-foreground mt-2">{info.email || "This address"} will no longer receive alumni emails from {b.university_name}.</p>
              <button data-testid="unsub-resubscribe" onClick={resubscribe} disabled={busy} className="mt-5 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{busy ? "Saving…" : "Re-subscribe to all"}</button>
            </div>
            )
          )}

          {info && info !== false && !info.opted_out && (
            <div data-testid="unsub-form">
              <p className="text-sm text-muted-foreground mb-4">{info.name ? `Hi ${info.name.split(" ")[0]}, choose` : "Choose"} which emails you'd like to receive{info.email ? <> at <b>{info.email}</b></> : null}.</p>
              <div className="space-y-3">
                <label data-testid="pref-events" className="flex items-start gap-3 p-3 rounded-md border border-border cursor-pointer hover:bg-muted/40">
                  <input type="checkbox" data-testid="pref-events-check" checked={events} onChange={(e) => setEvents(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#001A4B]" />
                  <span className="flex-1"><span className="flex items-center gap-1.5 font-medium text-sm"><CalendarHeart className="h-4 w-4 text-primary" /> Event invitations & reunions</span><span className="block text-xs text-muted-foreground mt-0.5">Homecoming, reunions, networking nights, and lectures.</span></span>
                </label>
                <label data-testid="pref-announcements" className="flex items-start gap-3 p-3 rounded-md border border-border cursor-pointer hover:bg-muted/40">
                  <input type="checkbox" data-testid="pref-announcements-check" checked={announcements} onChange={(e) => setAnnouncements(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#001A4B]" />
                  <span className="flex-1"><span className="flex items-center gap-1.5 font-medium text-sm"><Megaphone className="h-4 w-4 text-primary" /> Newsletters & announcements</span><span className="block text-xs text-muted-foreground mt-0.5">Community news, updates, and the alumni newsletter.</span></span>
                </label>
              </div>
              <button data-testid="pref-save" onClick={savePrefs} disabled={busy} className="mt-5 w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{busy ? "Saving…" : "Save Preferences"}</button>
              <button data-testid="unsub-confirm" onClick={unsubAll} disabled={busy} className="mt-3 w-full py-2 rounded-md border border-border text-sm text-muted-foreground hover:text-destructive hover:border-destructive/40">Unsubscribe from all emails</button>
              <div className="mt-4 pt-4 border-t border-border">
                <button data-testid="unsub-wrong-person" onClick={reportWrong} disabled={busy} className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm text-muted-foreground hover:text-primary">
                  <UserX className="h-4 w-4" /> This isn't me — you have the wrong person
                </button>
                <p className="text-[11px] text-muted-foreground text-center mt-1">We'll stop emailing this address and ask our team to review the record.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
